import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { Observable } from "rxjs";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Fiat } from "@models";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { StakingFlowStep } from "@modules/shared/domain/entities/trade-flow.model";
import { DEFAULT_AMOUNT_OPTION } from "@modules/staking/domain/entities/constants";
import { StakingAmountValidationService } from "@modules/staking/domain/+state/utility/staking-amount-validation.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { StakeableFilterOptions } from "@modules/staking/domain/infrastructure/staking.remote";
import { INFINITE_SEARCH_DELAY } from "@helpers/constants";
import { Router } from "@angular/router";

@UntilDestroy()
@Component({
	selector: "app-stakeable-selector",
	templateUrl: "./stakeable-selector.component.html",
	styleUrls: ["./stakeable-selector.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class StakeableSelectorComponent implements OnInit, OnDestroy {
	public stakeables$: Observable<Stakeable[]> = this.stakingFacade.getAvailableStakeables$();
	public hasError$: Observable<boolean> = this.stakingFacade.getErrorState$();
	public isLoading$: Observable<boolean> = this.stakingFacade.getLoadingState$();

	public filterOptions: BpSelectOption[] = [
		{
			name: "stakeable_filter_highest_yield",
			value: StakeableFilterOptions.YIELD_DESC
		},
		{
			name: "stakeable_filter_lowest_yield",
			value: StakeableFilterOptions.YIELD_ASC
		}
	];

	public searchInput: string = "";
	public searchInputDelay: number = INFINITE_SEARCH_DELAY;
	public selectedOption: string = this.filterOptions[0].value;

	@Input() earnOnStablecoinEnabled: boolean = false;

	public constructor(
		private stakingFacade: StakingFacade,
		private fiatService: FiatService,
		private userService: UserService,
		private infoService: InfoService,
		private translationService: TranslationService,
		private router: Router,
		private readonly stakingAmountValidationService: StakingAmountValidationService
	) {}

	public ngOnInit(): void {
		this.stakingFacade.resetStakingProcessAmount();
	}

	public ngOnDestroy(): void {
		//reset searched list on destroy
		this.fetchFilteredList("", this.filterOptions[0].value);
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById(this.userService.user.getFiatId());
	}

	public onSelect(stakeable: Stakeable): void {
		if (stakeable.availability === StakeableAvailability.SOLD_OUT) {
			return; // Items that are sold out should not be clickable
		}

		if (!this.stakingAmountValidationService.hasFundsToStake(stakeable)) {
			this.infoService.setError(this.translationService.translate("stake_buy_more_toaster", [stakeable.name]));
			return;
		}

		this.stakingFacade.updateStakingProcess({ stakeable, selectedPercentage: DEFAULT_AMOUNT_OPTION });
		this.stakingFacade.setWalletByTradableId(stakeable.id);
		this.stakingFacade.goToStep(StakingFlowStep.AMOUNT_SELECT);
	}

	public fetchStakingData(): void {
		this.stakingFacade.updateFilteredAssets(this.searchInput, this.selectedOption);
	}

	public fetchFilteredList(input: string = this.searchInput, sortOption: string = this.selectedOption): void {
		this.searchInput = input;
		this.selectedOption = sortOption;
		this.stakingFacade.updateFilteredAssets(input, sortOption);
	}
}
